import { useCallback, useEffect, useState } from "react";
import { api, payload } from "./lib/api";

interface Status {
  configured: boolean;
  connected: boolean;
  canManage: boolean;
  accountId: string | null;
  accountName: string | null;
  needsAccount: boolean;
}
interface Account { id: string; name: string; currency: string }
interface Campaign { id: string; name: string; status: string; type: string; cost: number; clicks: number; impressions: number }
interface Metrics {
  account: { id: string; name: string; currency: string };
  range: { days: number; startDate: string; endDate: string };
  totals: { cost: number; clicks: number; impressions: number; conversions: number };
  campaigns: Campaign[];
  site: { visitors: number; contacts: Record<string, number>; contactsTotal: number; costPerContact: number | null };
}

const integer = new Intl.NumberFormat("it-IT", { maximumFractionDigits: 0 });
const CAMPAIGN_TYPE: Record<string, string> = {
  SEARCH: "Ricerca", PERFORMANCE_MAX: "Performance Max", DISPLAY: "Display", SHOPPING: "Shopping",
  VIDEO: "Video", DEMAND_GEN: "Demand Gen", LOCAL: "Locale", SMART: "Smart",
};
const CONTACT_LABEL: Record<string, string> = { tel: "telefonate", whatsapp: "WhatsApp", form: "moduli", email: "email" };

function money(value: number, currency: string, digits = 2) {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: currency || "EUR", minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value);
}

export default function GoogleAdsPanel({ siteId, range }: { siteId: string; range: number }) {
  const [status, setStatus] = useState<Status | null>(null);
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [choice, setChoice] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [justConnected, setJustConnected] = useState(false);
  const days = range >= 90 ? 90 : range >= 30 ? 30 : 7;

  const load = useCallback(async () => {
    setError("");
    try {
      const next = await api<Status>(`/api/sites/${siteId}/integrations/google-ads`);
      setStatus(next);
      if (next.connected && next.accountId) {
        setAccounts(null);
        setMetrics(await api<Metrics>(`/api/sites/${siteId}/integrations/google-ads/metrics?range=${days}`));
      } else if (next.connected && next.canManage) {
        setMetrics(null);
        setAccounts((await api<{ accounts: Account[] }>(`/api/sites/${siteId}/integrations/google-ads/accounts`)).accounts);
      } else setMetrics(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Impossibile leggere Google Ads");
    }
  }, [siteId, days]);

  useEffect(() => {
    let active = true;
    // Rilettura iniziale: lo stato arriva dal server, non c'è nulla da salvare qui.
    void Promise.resolve().then(() => { if (active) void load(); });
    const params = new URLSearchParams(window.location.search);
    if (params.get("googleAds") === "connected") {
      params.delete("googleAds");
      window.history.replaceState(null, "", `${window.location.pathname}${params.toString() ? `?${params}` : ""}`);
      void Promise.resolve().then(() => { if (active) setJustConnected(true); });
    }
    return () => { active = false; };
  }, [load]);

  async function connect() {
    setBusy(true);
    setError("");
    try {
      const result = await api<{ authorizationUrl: string }>(`/api/sites/${siteId}/integrations/google-ads`, payload("POST"));
      window.location.assign(result.authorizationUrl);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Collegamento non riuscito");
      setBusy(false);
    }
  }

  async function confirmAccount() {
    if (!choice) return;
    setBusy(true);
    setError("");
    try {
      await api(`/api/sites/${siteId}/integrations/google-ads/account`, payload("POST", { customerId: choice }));
      setJustConnected(false);
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Non sono riuscito a salvare la scelta");
    } finally { setBusy(false); }
  }

  async function disconnect() {
    if (!window.confirm("Scollegare Google Ads da questo sito? Puoi ricollegarlo quando vuoi.")) return;
    setBusy(true);
    setError("");
    try {
      await api(`/api/sites/${siteId}/integrations/google-ads`, payload("DELETE"));
      setMetrics(null);
      setAccounts(null);
      setStatus((old) => (old ? { ...old, connected: false, accountId: null, accountName: null, needsAccount: false } : old));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Scollegamento non riuscito");
    } finally { setBusy(false); }
  }

  if (!status && !error) return <section className="ads-panel ads-loading">Controllo il collegamento con Google Ads…</section>;

  return <section className="ads-panel">
    <header>
      <div><p className="eyebrow indigo">Google Ads</p><h3>Quanto spendi e quanto ti costa ogni contatto</h3></div>
      {status?.connected && status.canManage && <button className="ads-link" onClick={disconnect} disabled={busy}>Scollega</button>}
    </header>

    {justConnected && <div className="ads-ok" role="status">Collegato. {status?.needsAccount ? "Ora scegli quale account mostrare." : ""}</div>}
    {error && <div className="notice error">{error}{/rinnovare|scaduto|Ricollega/i.test(error) && status?.canManage && <button className="ads-inline" onClick={connect} disabled={busy}>Ricollega</button>}</div>}

    {status && !status.connected && <div className="ads-connect">
      <div>
        <h4>Collega Google Ads in 3 passi</h4>
        <ol className="ads-steps">
          <li>Premi <strong>Collega Google Ads</strong>.</li>
          <li>Scegli il tuo account Google e premi <strong>Consenti</strong>: Google ti mostra che chiediamo <em>solo di leggere i rapporti</em>.</li>
          <li>Torni qui e scegli l'account pubblicitario. Fatto: vedi spesa, clic e costo per contatto.</li>
        </ol>
        <p className="ads-note">Non modifichiamo mai le tue campagne e non leggiamo email o file. Puoi scollegare in qualsiasi momento.</p>
      </div>
      <div className="ads-connect-action">
        {!status.configured ? <p className="ads-note">Il collegamento a Google non è ancora attivo su questo gestionale.</p>
          : status.canManage ? <button className="ads-primary" onClick={connect} disabled={busy}>{busy ? "Apro Google…" : "Collega Google Ads"}</button>
          : <p className="ads-note">Chiedi a chi gestisce il sito di collegare Google Ads: da qui puoi solo guardare.</p>}
      </div>
    </div>}

    {status?.connected && status.needsAccount && <div className="ads-choose">
      <h4>Scegli l'account pubblicitario da mostrare</h4>
      {!status.canManage ? <p className="ads-note">Un amministratore del sito deve scegliere l'account.</p>
        : accounts === null ? <p className="ads-note">Cerco i tuoi account…</p>
        : accounts.length === 0 ? <p className="ads-note">Questo account Google non ha accesso ad account Google Ads. Prova a ricollegare con l'account giusto.</p>
        : <>
          <div className="ads-accounts" role="radiogroup" aria-label="Account Google Ads">
            {accounts.map((account) => <label key={account.id} className={choice === account.id ? "selected" : ""}>
              <input type="radio" name="ads-account" value={account.id} checked={choice === account.id} onChange={() => setChoice(account.id)} />
              <span><strong>{account.name}</strong><small>{account.id.replace(/^(\d{3})(\d{3})(\d{4})$/, "$1-$2-$3")}{account.currency ? ` · ${account.currency}` : ""}</small></span>
            </label>)}
          </div>
          <button className="ads-primary" onClick={confirmAccount} disabled={!choice || busy}>Mostra questo account</button>
        </>}
    </div>}

    {metrics && <>
      <p className="ads-account">Account: <strong>{metrics.account.name}</strong> · ultimi {metrics.range.days} giorni</p>
      <div className="ads-kpis">
        <article><small>Spesa</small><strong>{money(metrics.totals.cost, metrics.account.currency)}</strong><span>{integer.format(metrics.totals.impressions)} visualizzazioni dell'annuncio</span></article>
        <article><small>Clic</small><strong>{integer.format(metrics.totals.clicks)}</strong><span>{metrics.totals.clicks ? `${money(metrics.totals.cost / metrics.totals.clicks, metrics.account.currency)} a clic` : "Nessun clic"}{metrics.site.visitors > 0 ? ` · ${integer.format(metrics.site.visitors)} visite registrate dal sito` : ""}</span></article>
        <article><small>Contatti dal sito</small><strong>{integer.format(metrics.site.contactsTotal)}</strong>
          <span>{metrics.site.contactsTotal ? Object.entries(metrics.site.contacts).filter(([, value]) => value > 0).map(([key, value]) => `${value} ${CONTACT_LABEL[key] || key}`).join(" · ") : "Telefono, WhatsApp e moduli da annunci"}</span></article>
        <article className="ads-main"><small>Costo per contatto</small><strong>{metrics.site.costPerContact === null ? "—" : money(metrics.site.costPerContact, metrics.account.currency)}</strong>
          <span>{metrics.site.costPerContact === null ? "Compare appena arriva un contatto da un annuncio" : "Spesa ÷ contatti arrivati dal sito"}</span></article>
      </div>
      {metrics.totals.clicks >= 5 && metrics.site.visitors === 0 && <p className="ads-hint">Google segna {integer.format(metrics.totals.clicks)} clic ma il sito non ha ancora registrato visite da annunci. Succede nei primi giorni dopo l'attivazione del tracciamento (parte da quando è stato acceso) o se nell'account Google Ads la «marcatura automatica» è spenta.</p>}
      {metrics.campaigns.length > 0 && <div className="ads-table" role="table" aria-label="Campagne">
        <div role="row" className="head"><span>Campagna</span><span>Tipo</span><span>Spesa</span><span>Clic</span></div>
        {metrics.campaigns.map((campaign) => <div role="row" key={campaign.id}>
          <span><b className={campaign.status === "ENABLED" ? "on" : ""} />{campaign.name}<small>{campaign.status === "ENABLED" ? "Attiva" : campaign.status === "PAUSED" ? "In pausa" : "Terminata"}</small></span>
          <span>{CAMPAIGN_TYPE[campaign.type] || campaign.type}</span>
          <span>{money(campaign.cost, metrics.account.currency)}</span>
          <span>{integer.format(campaign.clicks)}</span>
        </div>)}
      </div>}
      <p className="analytics-footnote">Spesa e clic vengono letti da Google ogni volta che apri questa pagina e non sono copiati altrove; i giorni seguono l'ora italiana. Clic e visite non coincidono mai al cento per cento (chi chiude la pagina prima che si carichi non viene contato dal sito). I contatti sono quelli registrati dal sito (telefono, WhatsApp, modulo) tra le visite arrivate da annunci Google: per riconoscerle serve la marcatura automatica attiva nell'account Google Ads.</p>
    </>}
  </section>;
}
